import { Box, Flex, HStack, Popover, Portal, Stack, Switch, Text, chakra } from '@chakra-ui/react';
import { RotateCcw, SlidersHorizontal } from 'lucide-react';

import type {
  DashboardControlsState,
  DashboardRowCount,
  RefreshIntervalSeconds,
  TableDensity,
} from '../hooks/useDashboardControls';
import { SelectMenu } from './SelectMenu';

interface DashboardControlsPopoverProps {
  controls: DashboardControlsState;
  onChange: (next: DashboardControlsState) => void;
  onReset: () => void;
}

const refreshOptions: readonly { value: RefreshIntervalSeconds; label: string }[] = [
  { value: 5, label: '5s' },
  { value: 10, label: '10s' },
  { value: 30, label: '30s' },
  { value: 60, label: '60s' },
];

const densityOptions: readonly { value: TableDensity; label: string }[] = [
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'compact', label: 'Compact' },
];

const rowOptions: readonly { value: DashboardRowCount; label: string }[] = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 15, label: '15' },
];

export function DashboardControlsPopover({
  controls,
  onChange,
  onReset,
}: DashboardControlsPopoverProps) {
  return (
    <Popover.Root positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
      <Popover.Trigger asChild>
        <chakra.button
          type="button"
          aria-label="Open dashboard controls"
          title="Dashboard controls"
          layerStyle="glassPanel"
          borderRadius="oqPanel"
          p="3"
          color="text.muted"
          transition="all 0.2s"
          _hover={{ color: 'text.primary' }}
        >
          <SlidersHorizontal size={16} />
        </chakra.button>
      </Popover.Trigger>

      <Portal>
        <Popover.Positioner>
          <Popover.Content
            aria-label="Dashboard controls"
            zIndex="popover"
            w={{ base: 'calc(100vw - 2rem)', md: '360px' }}
            borderRadius="oqPanel"
            borderWidth="1px"
            borderColor="border.subtle"
            bg="surface.panel"
            p="4"
            boxShadow="2xl"
          >
            <Flex align="flex-start" justify="space-between" gap="3" borderBottomWidth="1px" borderBottomColor="border.subtle" pb="3">
              <Box>
                <Text fontWeight="semibold" color="text.primary">
                  Dashboard controls
                </Text>
                <Text mt="1" fontSize="xs" color="text.muted">
                  Stored locally in this browser.
                </Text>
              </Box>
            </Flex>

            <Stack mt="4" gap="3">
              <Flex
                as="label"
                align="center"
                justify="space-between"
                gap="4"
                borderRadius="md"
                borderWidth="1px"
                borderColor="border.subtle"
                bg="blackAlpha.100"
                px="4"
                py="3"
              >
                <Box>
                  <Text as="span" display="block" fontSize="sm" fontWeight="semibold" color="text.primary">
                    Live feed
                  </Text>
                  <Text as="span" mt="0.5" display="block" fontSize="xs" color="text.muted">
                    When off, the table freezes at the last snapshot. Toggle back on to resume.
                  </Text>
                </Box>
                <Switch.Root
                  checked={controls.autoRefresh}
                  onCheckedChange={(details) => {
                    onChange({ ...controls, autoRefresh: details.checked });
                  }}
                  aria-label="Toggle live feed"
                  colorPalette="blue"
                  size="md"
                >
                  <Switch.HiddenInput />
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch.Root>
              </Flex>

              <SelectMenu
                label="Interval"
                value={controls.refreshIntervalSeconds}
                options={refreshOptions}
                onChange={(refreshIntervalSeconds) => {
                  onChange({ ...controls, refreshIntervalSeconds });
                }}
                portalled={false}
              />
              <SelectMenu
                label="Density"
                value={controls.tableDensity}
                options={densityOptions}
                onChange={(tableDensity) => {
                  onChange({ ...controls, tableDensity });
                }}
                portalled={false}
              />
              <SelectMenu
                label="Rows"
                value={controls.dashboardRowCount}
                options={rowOptions}
                onChange={(dashboardRowCount) => {
                  onChange({ ...controls, dashboardRowCount });
                }}
                helper="Visible row slots in the dashboard table."
                portalled={false}
              />
            </Stack>

            <chakra.button
              type="button"
              onClick={onReset}
              mt="4"
              display="flex"
              w="full"
              alignItems="center"
              justifyContent="center"
              gap="2"
              borderRadius="md"
              borderWidth="1px"
              borderColor="border.subtle"
              bg="blackAlpha.100"
              px="4"
              py="2.5"
              fontSize="sm"
              fontWeight="semibold"
              color="text.muted"
              transition="all 0.2s"
              _hover={{ color: 'text.primary' }}
            >
              <RotateCcw size={16} />
              Reset dashboard controls
            </chakra.button>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}